/** Own the Kuali connection disclosure so scope UI cannot accidentally absorb credentials. */
import { Trash2 } from 'lucide-react'

import { Button, Input, Label } from '@/components/ui'
import type { ConnectorCredentialSource } from '../../types'
import type { KualiResearchConnectionStatus } from '../types'
import { KualiResearchCredentials } from './KualiResearchCredentials'
import { KualiResearchDisclosure } from './KualiResearchDisclosure'

function summarizeApiUrl(value: string): string {
  try {
    const url = new URL(value)
    return `${url.host}${url.pathname.replace(/\/$/, '')}`
  } catch {
    return value || 'API URL required'
  }
}

export function KualiResearchConnectionSection({
  open,
  onOpenChange,
  apiUrl,
  onApiUrlChange,
  credentialSource,
  onCredentialSourceChange,
  connection,
  apiKey,
  onApiKeyChange,
  saving,
  canEditQuery,
  canManageCredentials,
  onDeleteCredential,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  apiUrl: string
  onApiUrlChange: (value: string) => void
  credentialSource: ConnectorCredentialSource
  onCredentialSourceChange: (value: ConnectorCredentialSource) => void
  connection: KualiResearchConnectionStatus
  apiKey: string
  onApiKeyChange: (value: string) => void
  saving: boolean
  canEditQuery: boolean
  canManageCredentials: boolean
  onDeleteCredential: () => void
}) {
  const credentialLabel = credentialSource === 'workspace'
    ? 'Workspace credential'
    : 'UMD Credential Store'
  const credentialStored = credentialSource === 'workspace'
    ? connection.workspaceConfigured === true
    : connection.umdConfigured === true

  return (
    <KualiResearchDisclosure
      title="Connection"
      summary={(
        <>
          <span className="font-mono">{summarizeApiUrl(apiUrl)}</span>
          <span className="mx-2 text-slate-300">•</span>{credentialLabel}
          {credentialStored && (
            <>
              <span className="mx-2 text-slate-300">•</span>
              <span className="text-emerald-700">✓ key stored</span>
            </>
          )}
        </>
      )}
      open={open}
      onOpenChange={onOpenChange}
    >
      <div className="space-y-3 border-t border-slate-100 px-3.5 pb-3.5 pt-3">
        <div className="space-y-1.5">
          <Label htmlFor="kuali-api-url" className="text-xs font-medium text-slate-700">API URL</Label>
          <Input
            id="kuali-api-url"
            type="url"
            autoComplete="url"
            value={apiUrl}
            disabled={!canEditQuery}
            onChange={event => onApiUrlChange(event.target.value)}
            placeholder="https://tenant.kuali.co/res"
            className="h-8 font-mono text-xs"
          />
        </div>
        <KualiResearchCredentials
          source={credentialSource}
          onSourceChange={onCredentialSourceChange}
          connection={connection}
          apiKey={apiKey}
          onApiKeyChange={onApiKeyChange}
          canSelectSource={canEditQuery}
          canManageCredential={canManageCredentials}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-0 text-xs text-destructive hover:bg-transparent hover:text-destructive"
          disabled={!canManageCredentials || credentialSource !== 'workspace' || !connection.workspaceConfigured || saving}
          onClick={onDeleteCredential}
        >
          <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete stored key
        </Button>
      </div>
    </KualiResearchDisclosure>
  )
}
